import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchResult from './SearchResult/SearchResult';
import { useMutation } from '@tanstack/react-query';
import debounce from '../../../Helper/debounce';
import style from './Search.module.scss?module';
import makeRequest from '../../../Helper/makeRequest';

const Search = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const searchRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState<string>('');
  const [active, setActive] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const shouldAbortSearchMutationRef = useRef<boolean>(true);

  const {
    mutateAsync: searchMutation,
    data: searchResultData,
    isPending,
  } = useMutation({
    mutationKey: ['search', query],
    mutationFn: async (queryStr: string) => {
      return makeRequest(`/api/search/${encodeURIComponent(queryStr)}`, 'GET');
    },
  });

  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam?.length > 0) {
      setQuery(queryParam);
    } else {
      setQuery('');
    }
  }, [searchParams.get('q')]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.target as Node)
    ) {
      setActive(false);
    }
  };

  const triggerSearch = useCallback(
    debounce((value: string) => {
      if (shouldAbortSearchMutationRef.current || !value) {
        setIsSearching(false);
        return;
      }

      searchMutation(value)
        .then(() => setActive(true))
        .catch(() => setActive(false))
        .finally(() => setIsSearching(false));
    }, 500),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setQuery(value);

    if (value) {
      setIsSearching(true);
      setActive(true);
    } else {
      setIsSearching(false);
      setActive(false);
    }

    shouldAbortSearchMutationRef.current = false;
    triggerSearch(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();

      handleSearchSubmit();
    }
  };

  function handleSearchSubmit() {
    shouldAbortSearchMutationRef.current = true;
    setIsSearching(false);

    navigate(`/products?q=${query}&sortBy=top`);
    setActive(false);
  }

  function handleRemoveQuery() {
    setQuery('');
    setActive(false);
    setIsSearching(false);
    shouldAbortSearchMutationRef.current = true;
    searchParams.delete('q');
    navigate(`/products?${searchParams.toString()}`);
    return;
  }

  return (
    <div
      ref={searchRef}
      className={[style.search, active ? style.active : ''].join(' ')}
    >
      <div className="position-relative">
        <input
          type="search"
          name="products and brands search"
          value={query}
          placeholder="Search products or brands..."
          className={style.searchInput}
          onFocus={() => {
            if (query) setActive(true);
            shouldAbortSearchMutationRef.current = false;
          }}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />

        {!query ? (
          <span className={style.searchInputIcon}>
            <FontAwesomeIcon icon={faSearch} />
          </span>
        ) : (
          <span className={style.searchInputIcon} onClick={handleRemoveQuery}>
            <FontAwesomeIcon icon={faTimes} />
          </span>
        )}
      </div>

      {active && (
        <SearchResult
          {...searchResultData}
          setActive={setActive}
          isFetching={isPending || isSearching}
          handleSearchSubmit={handleSearchSubmit}
        />
      )}
    </div>
  );
};

export default Search;
